/**
 * Shared utility functions for Schedule and Budget thermometer components.
 *
 * Extracted from ScheduleThermometer.tsx and BudgetThermometer.tsx to eliminate
 * duplication. Every function here was previously copy-pasted between both files.
 *
 * @module thermometerUtils
 */

import React, { useMemo, useRef, useState } from "react";
import { formatPercent } from '../../utils/numberFormat';
import { getProjectKpiConfigOverride } from '../../Data/ProjectConfig/projectKpiConfigOverrides';

// ────────────────────────────────────────────────
// Date conversion utilities
// ────────────────────────────────────────────────

/** Convert ISO date (yyyy-mm-dd) to US format (mm/dd/yyyy). */
export function isoToUS(iso?: string | null): string {
  if (!iso) return "";
  const s = String(iso).trim();
  const parts = s.split("-");
  if (parts.length !== 3) return "";
  const [y, m, d] = parts;
  if (!y || !m || !d) return "";
  return `${m.padStart(2, "0")}/${d.padStart(2, "0")}/${y}`;
}

/** Convert ISO date (yyyy-mm-dd) to dd-Mmm-yyyy format. */
export function isoToDdMmmYyyy(iso?: string | null): string {
  if (!iso) return "";
  const s = String(iso).trim();
  const parts = s.split("-");
  if (parts.length !== 3) return s;
  const [y, mRaw, dRaw] = parts;
  const mNum = Number(mRaw);
  const dNum = Number(dRaw);
  if (!y || !Number.isFinite(mNum) || !Number.isFinite(dNum)) return s;
  const months = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
  const mon = months[Math.max(1, Math.min(12, mNum)) - 1];
  const dd = String(dNum).padStart(2, "0");
  return `${dd}-${mon}-${y}`;
}

/** Convert US date (mm/dd/yyyy) to ISO format (yyyy-mm-dd). */
export function usToISO(us?: string | null): string | null {
  if (!us) return null;
  const s = String(us).trim();
  if (!s) return null;
  const parts = s.split("/");
  if (parts.length !== 3) return null;
  let [m, d, y] = parts.map((p) => p.trim());
  if (!y || !m || !d) return null;
  if (y.length === 2) y = `20${y}`;
  const mm = m.padStart(2, "0");
  const dd = d.padStart(2, "0");
  if (!/^[0-9]{4}$/.test(y) || !/^[0-9]{2}$/.test(mm) || !/^[0-9]{2}$/.test(dd))
    return null;
  return `${y}-${mm}-${dd}`;
}

/** Test whether a string is a valid ISO date (yyyy-mm-dd). */
export function isISODate(s?: string | null): boolean {
  if (!s) return false;
  return /^[0-9]{4}-[0-9]{2}-[0-9]{2}$/.test(String(s).trim());
}

/** Convert a JS Date to ISO string (yyyy-mm-dd) in local timezone. */
export function toISODate(d: Date) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

/** Parse ISO date string to milliseconds (local midnight). */
export function parseISODateToMs(iso: string) {
  const [y, m, d] = iso.split("-").map(Number);
  if (!y || !m || !d) return NaN;
  return new Date(y, m - 1, d).getTime();
}

// ────────────────────────────────────────────────
// Numeric / math utilities
// ────────────────────────────────────────────────

/** Clamp a value to [0, 1]. Non-finite values return 0. */
export function clamp01(v: number) {
  if (!Number.isFinite(v)) return 0;
  if (v < 0) return 0;
  if (v > 1) return 1;
  return v;
}

/** Clamp a value to [lo, hi]. Non-finite values return lo. */
export function clamp(v: number, lo: number, hi: number) {
  if (!Number.isFinite(v)) return lo;
  if (v < lo) return lo;
  if (v > hi) return hi;
  return v;
}

/**
 * Normalize a tolerance value to a fraction in [0, 1].
 * Accepts either whole-percent (e.g. 20 meaning 20%) or fraction (e.g. 0.2).
 */
export function normalizeToleranceToFraction(v: any): number | null {
  const n = Number(v);
  if (!Number.isFinite(n)) return null;
  if (n <= 0) return null;
  if (n <= 1.0000001) return clamp(n, 0, 1);
  // Treat as percent.
  return clamp(n / 100, 0, 1);
}

// ────────────────────────────────────────────────
// Formatting utilities
// ────────────────────────────────────────────────

/** Format a value as a percentage string, respecting the data scale factor. */
export function fmtPct(v: number, scale: number) {
  const pct = scale === 100 ? v : v * 100;
  const rounded = Math.round(pct * 10) / 10;
  return Number.isInteger(rounded)
    ? formatPercent(rounded, 0)
    : formatPercent(rounded, 1);
}

/** Format an ISO date (yyyy-mm-dd) as a display string (dd-Mmm-yyyy). */
export function fmtAsOfDate(dateISO: string) {
  const s = String(dateISO || "").trim();
  const m = s.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!m) return s;

  const yyyy = Number(m[1]);
  const mm = Number(m[2]);
  const dd = Number(m[3]);
  const months = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];

  if (!Number.isFinite(yyyy) || !Number.isFinite(mm) || !Number.isFinite(dd))
    return s;
  if (mm < 1 || mm > 12) return s;

  const dd2 = String(dd).padStart(2, "0");
  return `${dd2}-${months[mm - 1]}-${yyyy}`;
}

// ────────────────────────────────────────────────
// KPI config utilities
// ────────────────────────────────────────────────

/**
 * Read variance tolerance thresholds from a KPI config override for a given instance.
 * @param categoryPreference - Which category to check first ("schedule" or "finance").
 */
export function readVarianceTolerancesFromInstance(
  projectGuid: string,
  instanceGuid: string,
  displayName?: string,
  categoryPreference: string = "schedule",
): { yellow_pp?: number; red_pp?: number; progress_maturity_guard_pct?: number } | null {
  try {
    const pg = String(projectGuid || "").trim();
    const ig = String(instanceGuid || "").trim();
    const dn = String(displayName || "").trim();
    if (!pg) return null;

    const ov = getProjectKpiConfigOverride(pg) as any;
    const cats = (ov as any)?.category_instances || {};

    const findIn = (arr: any[] | undefined | null) => {
      const a = arr || [];
      if (ig) {
        const hit = a.find((x: any) => String(x?.instance_guid) === ig);
        if (hit) return hit;
      }
      if (dn) {
        const hit = a.find((x: any) => String(x?.display_name) === dn);
        if (hit) return hit;
      }
      return null;
    };

    // Prefer the specified category when present.
    const preferred = findIn((cats as any)?.[categoryPreference]);
    const inst =
      preferred ||
      Object.keys(cats || {})
        .map((k) => findIn((cats as any)?.[k]))
        .find((x) => !!x);

    if (!inst) return null;
    return {
      yellow_pp: (inst as any)?.variance_yellow_pp,
      red_pp: (inst as any)?.variance_red_pp,
      progress_maturity_guard_pct: (inst as any)?.progress_maturity_guard_pct,
    };
  } catch {
    return null;
  }
}

// ────────────────────────────────────────────────
// SVG path builders (generic over data point type)
// ────────────────────────────────────────────────

/**
 * Build an SVG line path from data rows.
 * Generic over the row type so both Schedule and Finance points work.
 */
export function buildPath<T>(
  rows: T[],
  getYRaw: (r: T) => number | null,
  xForRow: (r: T) => number,
  yForValue01: (v01: number) => number,
  scaleFactor: number,
) {
  if (!rows.length) return "";
  const parts: string[] = [];
  let started = false;

  for (const r of rows) {
    const raw = getYRaw(r);
    if (raw === null || raw === undefined || !Number.isFinite(Number(raw))) {
      started = false;
      continue;
    }
    const x = xForRow(r);
    const v01 = clamp01(Number(raw) / scaleFactor);
    const y = yForValue01(v01);
    parts.push(`${started ? "L" : "M"} ${x.toFixed(2)} ${y.toFixed(2)}`);
    started = true;
  }

  return parts.join(" ");
}

/**
 * Build an SVG band (area) path from data rows — used for tolerance bands.
 * Generic over the row type so both Schedule and Finance points work.
 */
export function buildBandPath<T>(
  rows: T[],
  getCenterRaw: (r: T) => number | null,
  getHalfWidth01: (r: T) => number,
  xForRow: (r: T) => number,
  yForValue01: (v01: number) => number,
  scaleFactor: number,
) {
  const usable = (rows || []).filter((r) => {
    const c = getCenterRaw(r);
    return c !== null && c !== undefined && Number.isFinite(Number(c));
  });
  if (usable.length < 2) return "";

  const tops: string[] = [];
  const bots: string[] = [];

  for (const r of usable) {
    const x = xForRow(r);
    const cRaw = getCenterRaw(r);
    if (cRaw === null || cRaw === undefined) continue;

    const c01 = clamp01(cRaw / scaleFactor);
    const hw = clamp(getHalfWidth01(r), 0, 0.35);

    const top = yForValue01(clamp01(c01 + hw));
    const bot = yForValue01(clamp01(c01 - hw));

    tops.push(`${x.toFixed(2)} ${top.toFixed(2)}`);
    bots.push(`${x.toFixed(2)} ${bot.toFixed(2)}`);
  }

  if (tops.length < 2 || bots.length < 2) return "";

  // Area path: top left->right then bottom right->left.
  const first = tops[0];
  const d = [
    `M ${first}`,
    `L ${tops.slice(1).join(" L ")}`,
    `L ${bots.slice().reverse().join(" L ")}`,
    "Z",
  ];
  return d.join(" ");
}

// ────────────────────────────────────────────────
// Shared UI components
// ────────────────────────────────────────────────

/**
 * Small "i" icon with hover tooltip. Used in thermometer chart headers
 * to explain metrics like SPI, variance bands, etc.
 */
export function InfoIcon({ title }: { title: string }) {
  const [open, setOpen] = useState(false);

  return (
    <span
      style={{
        position: "relative",
        display: "inline-flex",
        alignItems: "center",
      }}
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
      onFocus={() => setOpen(true)}
      onBlur={() => setOpen(false)}
    >
      <span
        title={title}
        style={{
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          width: 16,
          height: 16,
          borderRadius: 999,
          border: "1px solid var(--border-strong)",
          background: "var(--bg-hover)",
          fontSize: 11,
          lineHeight: "16px",
          marginInlineStart: 6,
          opacity: 0.9,
          cursor: "help",
          userSelect: "none",
        }}
        aria-label="Info"
        tabIndex={0}
      >
        i
      </span>

      {open ? (
        <div
          role="tooltip"
          style={{
            position: "absolute",
            top: "calc(100% + 6px)",
            insetInlineStart: 0,
            zIndex: 50,
            minWidth: 220,
            maxWidth: 360,
            padding: "8px 10px",
            borderRadius: 10,
            border: "1px solid var(--border-default)",
            background: "var(--bg-secondary)",
            boxShadow: "0 10px 30px rgba(0,0,0,0.35)",
            fontSize: 12,
            lineHeight: 1.35,
            color: "var(--text-primary)",
            whiteSpace: "normal",
          }}
        >
          {title}
        </div>
      ) : null}
    </span>
  );
}

// ────────────────────────────────────────────────
// Baseline interpolation utility
// ────────────────────────────────────────────────

/**
 * Interpolate a baseline value at a specific timestamp using linear interpolation.
 * Used by both Schedule and Budget thermometers to compute "baseline at as-of date"
 * for variance/delta calculations.
 *
 * @param baselineRows  Array of rows with reporting_date and a numeric baseline field.
 * @param targetMs      Target timestamp in milliseconds (local midnight).
 * @param getBaseline   Accessor to extract the raw baseline value from a row.
 * @param scaleFactor   Scale factor for normalization (converts raw → pct100).
 * @returns             Interpolated baseline as pct100, or null if insufficient data.
 */
export function interpolateBaselineAtDate<T extends { reporting_date: string }>(
  baselineRows: T[],
  targetMs: number,
  getBaseline: (r: T) => number,
  scaleFactor: number,
): number | null {
  if (!Number.isFinite(targetMs) || !baselineRows.length) return null;

  const pts = baselineRows
    .map((r) => {
      const ms = isISODate(String(r.reporting_date))
        ? parseISODateToMs(String(r.reporting_date))
        : NaN;
      const vRaw = getBaseline(r);
      const v100 = scaleFactor > 0 ? (vRaw / scaleFactor) * 100 : 0;
      return { ms, v100 };
    })
    .filter((p) => Number.isFinite(p.ms) && Number.isFinite(p.v100))
    .sort((a, b) => a.ms - b.ms);

  if (!pts.length) return null;

  // Exact match
  for (const p of pts) {
    if (p.ms === targetMs) return Math.max(0, p.v100);
  }

  // Find neighbors for linear interpolation
  let before: { ms: number; v100: number } | null = null;
  let after: { ms: number; v100: number } | null = null;
  for (const p of pts) {
    if (p.ms < targetMs) before = p;
    if (p.ms > targetMs) { after = p; break; }
  }
  if (before && after) {
    const t = (targetMs - before.ms) / (after.ms - before.ms);
    return Math.max(0, before.v100 + t * (after.v100 - before.v100));
  }
  if (before) return Math.max(0, before.v100);
  if (after) return Math.max(0, after.v100);
  return null;
}

// ────────────────────────────────────────────────
// Row filtering hooks
// ────────────────────────────────────────────────

/**
 * Configuration for splitting a time series into actual/forecast/baseline row sets.
 * Provide field accessor functions for the domain-specific column names.
 */
export interface RowFilterAccessors<T> {
  getActual: (r: T) => number | null | undefined;
  getForecast: (r: T) => number | null | undefined;
  getBaseline: (r: T) => number | null | undefined;
  getDate: (r: T) => string;
}

/**
 * Split a date-filtered series into actual, forecast, and baseline row sets.
 * This replaces the triplicate useMemo blocks duplicated in both thermometers.
 */
export function useRowFilters<T>(
  dateFilteredSeries: T[],
  todayISO: string,
  accessors: RowFilterAccessors<T>,
) {
  const actualRows = useMemo(
    () =>
      dateFilteredSeries.filter(
        (r) =>
          accessors.getDate(r) <= todayISO &&
          accessors.getActual(r) !== null &&
          accessors.getActual(r) !== undefined &&
          Number.isFinite(Number(accessors.getActual(r))),
      ),
    [dateFilteredSeries, todayISO, accessors],
  );

  const forecastRows = useMemo(
    () =>
      dateFilteredSeries.filter(
        (r) =>
          accessors.getDate(r) > todayISO &&
          accessors.getForecast(r) !== null &&
          accessors.getForecast(r) !== undefined &&
          Number.isFinite(Number(accessors.getForecast(r))),
      ),
    [dateFilteredSeries, todayISO, accessors],
  );

  const baselineRows = useMemo(
    () =>
      dateFilteredSeries.filter(
        (r) =>
          accessors.getBaseline(r) !== null &&
          accessors.getBaseline(r) !== undefined &&
          Number.isFinite(Number(accessors.getBaseline(r))),
      ),
    [dateFilteredSeries, accessors],
  );

  const actualLatest = actualRows.length
    ? actualRows[actualRows.length - 1]
    : dateFilteredSeries.length
    ? dateFilteredSeries[dateFilteredSeries.length - 1]
    : null;

  return { actualRows, forecastRows, baselineRows, actualLatest };
}

// ────────────────────────────────────────────────
// Date-range filter
// ────────────────────────────────────────────────

/**
 * Filter series rows to only include those within the project date window.
 * Prevents stale DB records from appearing on the chart after a date-range change.
 */
export function useDateFilteredSeries<T extends { reporting_date: string }>(
  series: T[],
  startISO: string,
  endISO: string,
): T[] {
  return useMemo(() => {
    if (!startISO && !endISO) return series;
    return series.filter((r) => {
      const d = r.reporting_date;
      if (startISO && d < startISO) return false;
      if (endISO && d > endISO) return false;
      return true;
    });
  }, [series, startISO, endISO]);
}

// ────────────────────────────────────────────────
// Variance segment builder
// ────────────────────────────────────────────────

/** A colored line segment between two data points. */
export interface VarianceSegment {
  x1: number;
  y1: number;
  x2: number;
  y2: number;
  color: string;
}

/**
 * Build variance-colored line segments from actual data rows.
 * The segment between each pair of consecutive actual points is colored
 * green/yellow/red based on how far actual deviates from baseline.
 *
 * @param actualRows     Filtered actual data rows in chronological order.
 * @param baselineMap    Map of reporting_date → baseline raw value.
 * @param getActual      Accessor to extract the raw actual value from a row.
 * @param xForRow        Function to compute X coordinate for a row.
 * @param yForValue01    Function to convert a [0,1] normalized value to Y coordinate.
 * @param scaleFactor    Scale factor for normalization.
 * @param direction      "increasing" or "decreasing" — controls behindPP sign convention.
 * @param costDirection  If true, use cost direction (over-budget = bad when increasing).
 * @param tolYellowPP    Warning threshold in percentage points.
 * @param tolRedPP       Critical threshold in percentage points.
 */
export function buildVarianceSegments<T>(
  actualRows: T[],
  baselineMap: Map<string, number>,
  getActual: (r: T) => number,
  getDate: (r: T) => string,
  xForRow: (r: T) => number,
  yForValue01: (v01: number) => number,
  scaleFactor: number,
  direction: string,
  costDirection: boolean,
  tolYellowPP: number,
  tolRedPP: number,
): VarianceSegment[] {
  const segments: VarianceSegment[] = [];

  for (let i = 1; i < actualRows.length; i++) {
    const prev = actualRows[i - 1];
    const curr = actualRows[i];

    const prevActual = Number(getActual(prev));
    const currActual = Number(getActual(curr));
    if (!Number.isFinite(prevActual) || !Number.isFinite(currActual)) continue;

    const currBaseline = baselineMap.get(getDate(curr));

    let segColor = "#2ecc71"; // default: on track

    if (currBaseline !== undefined) {
      const blPct100 = scaleFactor === 100 ? currBaseline : currBaseline * 100;
      const actPct100 = scaleFactor === 100 ? currActual : currActual * 100;

      // Direction-aware deviation:
      // Schedule: behind = baseline - actual (increasing) or actual - baseline (decreasing)
      // Cost:     behind = actual - baseline (increasing) or baseline - actual (decreasing)
      const segBehindPP = costDirection
        ? (direction === "decreasing" ? blPct100 - actPct100 : actPct100 - blPct100)
        : (direction === "decreasing" ? actPct100 - blPct100 : blPct100 - actPct100);

      if (segBehindPP >= tolRedPP) {
        segColor = "#e74c3c"; // critical
      } else if (segBehindPP >= tolYellowPP) {
        segColor = "#f1c40f"; // warning
      }
    }

    const x1 = xForRow(prev);
    const y1 = yForValue01(clamp01(prevActual / scaleFactor));
    const x2 = xForRow(curr);
    const y2 = yForValue01(clamp01(currActual / scaleFactor));

    segments.push({ x1, y1, x2, y2, color: segColor });
  }

  return segments;
}

// ────────────────────────────────────────────────
// Forecast band width calculation
// ────────────────────────────────────────────────

/**
 * Estimate confidence band half-widths for forecast rendering.
 * Uses recent actual-vs-baseline volatility to size the band.
 */
export function useForecastBandWidths<T extends { reporting_date: string }>(
  series: T[],
  getActual: (r: T) => number,
  getBaseline: (r: T) => number,
  scaleFactor: number,
  todayISO: string,
): Map<string, number> {
  return useMemo(() => {
    if (!series.length) return new Map<string, number>();

    const usable = series
      .filter(
        (r) => Number.isFinite(getActual(r)) && Number.isFinite(getBaseline(r)),
      )
      .slice(-10);

    const diffs01: number[] = [];
    for (const r of usable) {
      const a01 = clamp01(getActual(r) / scaleFactor);
      const b01 = clamp01(getBaseline(r) / scaleFactor);
      diffs01.push(a01 - b01);
    }
    const mean = diffs01.length
      ? diffs01.reduce((s, v) => s + v, 0) / diffs01.length
      : 0;
    const var01 = diffs01.length
      ? diffs01.reduce((s, v) => {
          const d = v - mean;
          return s + d * d;
        }, 0) / diffs01.length
      : 0;
    const sigma = Math.sqrt(var01);

    const baseHw = clamp(sigma * 1.5, 0.03, 0.18);

    const mp = new Map<string, number>();
    for (const r of series) {
      const isFuture = r.reporting_date > todayISO;
      const hw = isFuture
        ? clamp(baseHw * 1.25, 0.03, 0.22)
        : clamp(baseHw * 0.9, 0.02, 0.18);
      mp.set(r.reporting_date, hw);
    }
    return mp;
  }, [series, scaleFactor, todayISO, getActual, getBaseline]);
}

// ────────────────────────────────────────────────
// Shared UI components — ThermometerTooltip
// ────────────────────────────────────────────────

import styles from "./Navigation.module.css";

/** Configuration for a single row in the thermometer tooltip. */
export interface TooltipRowConfig {
  label: string;
  value: number | null | undefined;
  format: (v: number) => string;
}

/**
 * Shared tooltip component for both Schedule and Budget thermometers.
 * Renders a floating tooltip with date header and configurable data rows.
 */
export function ThermometerTooltip({
  date,
  rows,
  x,
  y,
}: {
  date: string;
  rows: TooltipRowConfig[];
  x: number;
  y: number;
}) {
  return (
    <div
      className={styles.scheduleTooltip}
      style={{
        insetInlineStart: x + 12,
        top: y + 12,
      }}
    >
      <div className={styles.scheduleTooltipDate}>{date}</div>
      {rows.map((row, i) => (
        <div key={i} className={styles.scheduleTooltipRow}>
          <span className={styles.scheduleTooltipLabel}>{row.label}</span>
          <span className={styles.scheduleTooltipValue}>
            {row.value === null || row.value === undefined
              ? "—"
              : row.format(Number(row.value))}
          </span>
        </div>
      ))}
    </div>
  );
}

// ────────────────────────────────────────────────
// Shared UI components — ThermometerLegend
// ────────────────────────────────────────────────

/** Configuration for a single legend item. */
export interface LegendItemConfig {
  label: string;
  className: string;
}

/**
 * Shared legend component for both Schedule and Budget thermometers.
 * Renders the colored swatch + label list below the chart.
 */
export function ThermometerLegend({
  items,
  hint,
}: {
  items: LegendItemConfig[];
  hint?: string;
}) {
  return (
    <div className={styles.scheduleChartLegend}>
      {items.map((item, i) => (
        <div key={i} className={styles.legendItem}>
          <span className={`${styles.legendSwatch} ${item.className}`} />
          <span>{item.label}</span>
        </div>
      ))}
      {hint ? (
        <div className={styles.legendHint}>{hint}</div>
      ) : null}
    </div>
  );
}

// ────────────────────────────────────────────────
// Shared UI components — PipelineStatusLog
// ────────────────────────────────────────────────

/** A single entry in the pipeline status log. */
export type StatusEntry = {
  ts: string;
  level: "info" | "warn" | "error" | "success";
  msg: string;
};

/**
 * Hook for pipeline status log state + push helper.
 * Used in both thermometers for debug-mode logging during Apply operations.
 */
export function useStatusLog() {
  const [statusLog, setStatusLog] = useState<StatusEntry[]>([]);
  const statusLogRef = useRef<HTMLDivElement>(null);

  const pushStatus = (level: StatusEntry["level"], msg: string) => {
    const ts = new Date().toLocaleTimeString("en-US", {
      hour12: false,
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    });
    setStatusLog((prev) => [...prev, { ts, level, msg }]);
    setTimeout(() => statusLogRef.current?.scrollTo({ top: 999999 }), 50);
  };

  return { statusLog, setStatusLog, statusLogRef, pushStatus };
}

/**
 * Shared pipeline status log renderer (debug mode only).
 * Identical between Schedule and Budget thermometers.
 */
export function PipelineStatusLog({
  statusLog,
  statusLogRef,
  onClear,
}: {
  statusLog: StatusEntry[];
  statusLogRef: React.RefObject<HTMLDivElement>;
  onClear: () => void;
}) {
  if (!statusLog.length) return null;

  return (
    <div
      ref={statusLogRef}
      style={{
        marginTop: 8,
        maxHeight: 220,
        overflowY: "auto",
        border: "1px solid var(--border-default)",
        borderRadius: 10,
        background: "var(--bg-elevated)",
        padding: "8px 10px",
        fontFamily: "'SF Mono', 'Fira Code', 'Consolas', monospace",
        fontSize: 11,
        lineHeight: 1.5,
      }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 6,
        }}
      >
        <span
          style={{
            fontWeight: 700,
            fontSize: 10,
            textTransform: "uppercase",
            letterSpacing: 1,
            color: "var(--text-tertiary)",
          }}
        >
          Pipeline Status Log
        </span>
        <button
          type="button"
          onClick={onClear}
          style={{
            padding: "2px 8px",
            borderRadius: 6,
            border: "1px solid var(--border-default)",
            background: "var(--bg-hover)",
            color: "var(--text-tertiary)",
            cursor: "pointer",
            fontSize: 10,
          }}
        >
          Clear
        </button>
      </div>
      {statusLog.map((entry, i) => (
        <div
          key={i}
          style={{
            color:
              entry.level === "error"
                ? "rgba(231,76,60,0.95)"
                : entry.level === "warn"
                ? "rgba(241,196,15,0.95)"
                : entry.level === "success"
                ? "rgba(46,204,113,0.95)"
                : "var(--text-secondary)",
          }}
        >
          <span style={{ opacity: 0.5 }}>[{entry.ts}]</span> {entry.msg}
        </div>
      ))}
    </div>
  );
}
